import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  listPlatformTenants,
  readSession,
  type PlatformTenantSummary,
} from '../../lib/api.js';

type TenantsListProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; tenants: PlatformTenantSummary[]; total: number };

const PAGE_SIZE = 50;

const STATUS_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'provisioning', label: 'Provisioning' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'deactivating', label: 'Deactivating' },
  { value: 'deactivated', label: 'Deactivated' },
];

const STATUS_LABEL: Record<string, string> = {
  provisioning: 'Provisioning',
  active: 'Active',
  suspended: 'Suspended',
  deactivating: 'Deactivating',
  deactivated: 'Deactivated',
  purged: 'Purged',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export function TenantsList({ config }: TenantsListProps): JSX.Element {
  const [status, setStatus] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const apiUrl = config.deployment.apiUrl;

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const res = await listPlatformTenants(apiUrl, token, {
        limit: PAGE_SIZE,
        offset,
        status: status || undefined,
      });
      setState({ kind: 'ready', tenants: res.tenants, total: res.total });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load tenants';
      setState({ kind: 'error', message });
    }
  }, [apiUrl, offset, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const pageInfo = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const start = state.tenants.length === 0 ? 0 : offset + 1;
    const end = offset + state.tenants.length;
    return `${start}–${end} of ${state.total}`;
  }, [state, offset]);

  const loading = state.kind === 'loading';
  const hasPrev = offset > 0;
  const hasNext = state.kind === 'ready' && offset + state.tenants.length < state.total;

  return (
    <section className="ferro-admin-page" aria-busy={loading}>
      <div className="ferro-section__header">
        <h1 className="ferro-section__title">Tenants</h1>
        <Link to="/platform/tenants/new" className="ferro-btn ferro-btn--primary">
          New tenant
        </Link>
      </div>
      <p className="ferro-muted">
        All tenants currently registered on this platform.
      </p>

      <div className="ferro-toolbar">
        <label className="ferro-field">
          <span className="ferro-field__label">Status</span>
          <select
            className="ferro-input"
            value={status}
            onChange={(e) => {
              setOffset(0);
              setStatus(e.target.value);
            }}
          >
            {STATUS_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading…
        </p>
      ) : state.kind === 'error' ? (
        <p className="ferro-error" role="alert">
          {state.message}
        </p>
      ) : state.tenants.length === 0 ? (
        <p className="ferro-muted">No tenants match the current filter.</p>
      ) : (
        <table className="ferro-table" data-testid="tenants-list-table">
          <thead>
            <tr>
              <th scope="col">Display name</th>
              <th scope="col">Slug</th>
              <th scope="col">Status</th>
              <th scope="col">Activated</th>
              <th scope="col">Created</th>
            </tr>
          </thead>
          <tbody>
            {state.tenants.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/platform/tenants/${t.id}`}>{t.displayName}</Link>
                </td>
                <td className="ferro-mono">{t.slug}</td>
                <td>{STATUS_LABEL[t.status] ?? t.status}</td>
                <td>{formatDate(t.activatedAt)}</td>
                <td>{formatDate(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="ferro-toolbar">
        {pageInfo ? <span className="ferro-muted">{pageInfo}</span> : null}
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          disabled={!hasPrev || loading}
          onClick={() => setOffset((o) => Math.max(o - PAGE_SIZE, 0))}
        >
          Previous
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          disabled={!hasNext || loading}
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
        >
          Next
        </button>
      </div>
    </section>
  );
}
